import React, { useEffect, useRef, useState } from "react";
import { get } from "../lib/api";
import { useDebounced } from "../lib/hooks";
import { Input, TierBadge } from "../ui";
import { HiddenLabel } from "./Hidden";

const ENDPOINT = { contact: "v1/crm/contacts", company: "v1/crm/companies" };

// Type-ahead over CRM contacts or companies. value is {id, name} or null.
// disabled: show the value (or a dash) read-only.
export default function EntityPicker({ kind, value, onChange, placeholder, autoFocus, onCreate, exclude, disabled }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [itemsFor, setItemsFor] = useState(null); // the query the items answer
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(false);
    const dq = useDebounced(q, 200);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        // Hidden records (E12) leave the main lists only: keep them pickable.
        get(ENDPOINT[kind], { q: dq, page_size: 8, hidden: "include" })
            .then((res) => {
                if (cancelled) return;
                setItems((res.data || []).filter((x) => !exclude || x.id !== exclude));
                setItemsFor(dq);
                setActive(0);
            })
            .catch(() => !cancelled && setItems([]))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [dq, open, kind, exclude]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (disabled && !value) return <span className="hz-picked hz-muted">–</span>;
    if (value) {
        return (
            <span className="hz-picked">
                <i className={kind === "contact" ? "ri-user-line" : "ri-building-line"}></i>
                <span className="hz-ellipsis">{value.name}</span>
                {!disabled && (
                    <button type="button" aria-label="Clear" onClick={() => onChange(null)}>
                        <i className="ri-close-line"></i>
                    </button>
                )}
            </span>
        );
    }

    const pick = (item) => {
        onChange(item);
        setQ("");
        setOpen(false);
    };
    const showCreate = onCreate && q.trim().length > 1 && !items.some((i) => i.name.toLowerCase() === q.trim().toLowerCase());

    return (
        <div className="hz-picker" ref={ref}>
            <Input
                value={q}
                autoFocus={autoFocus}
                placeholder={placeholder || (kind === "contact" ? "Search people" : "Search companies")}
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                    setQ(e.target.value);
                    setOpen(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setActive((a) => Math.min(a + 1, items.length - 1));
                    } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setActive((a) => Math.max(a - 1, 0));
                    } else if (e.key === "Enter") {
                        // Only pick from results that answer what is typed now.
                        e.preventDefault();
                        if (itemsFor === q && items[active]) pick(items[active]);
                    } else if (e.key === "Escape" && open) {
                        e.stopPropagation();
                        setOpen(false);
                    }
                }}
            />
            {open && (
                <div className="hz-picker-list">
                    {loading && items.length === 0 && <div className="hz-empty-sm">Searching…</div>}
                    {!loading && items.length === 0 && !showCreate && <div className="hz-empty-sm">No matches.</div>}
                    {items.map((it, i) => (
                        <button type="button" key={it.id} className={"hz-picker-opt" + (i === active ? " active" : "")} onMouseEnter={() => setActive(i)} onClick={() => pick(it)}>
                            <i className={kind === "contact" ? "ri-user-line hz-muted" : "ri-building-line hz-muted"}></i>
                            <span className="hz-spacer hz-ellipsis">
                                {it.name}
                                <span className="hz-small hz-muted">
                                    {kind === "contact" && it.current_role ? ` · ${it.current_role.title || ""}${it.current_role.company_name ? " at " + it.current_role.company_name : ""}` : ""}
                                    {kind === "company" && it.domain ? ` · ${it.domain}` : ""}
                                </span>
                            </span>
                            <HiddenLabel at={it.hidden_at} source={it.hidden_source} />
                            {kind === "contact" && <TierBadge tier={it.tier} />}
                        </button>
                    ))}
                    {showCreate && (
                        <button type="button" className="hz-picker-opt" onClick={() => onCreate(q.trim())}>
                            <i className="ri-add-line hz-muted"></i>
                            Create "{q.trim()}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
